import { Workbox } from 'workbox-window';
import Callback from './Callback';

const allowLocally = true;

class ServiceWorker {
    constructor() {
        if (ServiceWorker.instance) {
            return ServiceWorker.instance;
        }

        this.supported = (
            'serviceWorker' in navigator &&
            (window.location.protocol === 'https:' || (window.location.hostname === 'localhost' && allowLocally))
        );

        this.wb = null;
        this.statusChangeCallback = new Callback();
        this.status = {
            supported: null,
            enabled: null,
            updateAvailable: null,
            status: 'Unknown',
        };

        if (!this.supported) {
            this.updateStatus({
                supported: false,
                enabled: false,
                status: 'Not Supported',
                updateAvailable: false,
            });
            return;
        }


        this.updateStatus({
            supported: this.supported,
            enabled: false,
            status: 'Available',
            updateAvailable: false,
        });

        // Check for existing registration
        this.checkExistingRegistration();

        ServiceWorker.instance = this;
    }

    updateStatus(changes) {
        this.status = { ...this.status, ...changes };
        this.statusChangeCallback.invoke(this.status);
    }

    onStatusChange(callback) {
        const remove = this.statusChangeCallback.listen(callback);
        this.statusChangeCallback.invoke(this.status);
        return remove;
    }

    async checkExistingRegistration() {
        try {
            const registration = await navigator.serviceWorker.getRegistration('/service-worker.js');
            if (registration) {
                this.wb = new Workbox('/service-worker.js');
                this.setupEventListeners();
                this.updateStatus({
                    enabled: true,
                    status: 'Installed'
                });
            }

            if (window.localStorage.getItem('sw:installed')) {
                this.install();
            }
        } catch (error) {
            console.error('Failed to check service worker registration:', error);
        }
    }

    async install() {
        if (!this.supported) {
            return Promise.resolve();
        }

        window.localStorage.setItem('sw:installed', 'true');

        return new Promise((resolve, reject) => {
            try {
                this.wb = new Workbox('/service-worker.js');
                this.setupEventListeners();

                this.wb.register()
                    .then(() => {
                        this.updateStatus({
                            enabled: true,
                            status: 'Installed'
                        });
                        resolve();
                    })
                    .catch((error) => {
                        console.error('Failed to install service worker:', error);
                        this.updateStatus({
                            enabled: false,
                            status: 'Failed'
                        });
                        reject(error);
                    });
            } catch (error) {
                console.error('Failed to install service worker:', error);
                this.updateStatus({
                    enabled: false,
                    status: 'Failed'
                });
                reject(error);
            }
        });
    }

    async uninstall() {
        window.localStorage.removeItem('sw:installed');

        return new Promise((resolve, reject) => {
            navigator.serviceWorker.getRegistration('/service-worker.js')
                .then(registration => {
                    if (registration) {
                        return registration.unregister();
                    }
                    return Promise.resolve();
                })
                .then(() => {
                    this.wb = null;
                    this.updateStatus({
                        enabled: false,
                        status: 'Uninstalled'
                    });
                    resolve();
                })
                .catch(error => {
                    console.error('Failed to unregister service worker:', error);
                    reject(error);
                });
        });
    }

    setupEventListeners() {
        if (!this.wb) {
            return;
        }

        this.wb.addEventListener('waiting', () => {
            this.updateStatus({
                updateAvailable: true
            });
        });

        this.wb.addEventListener('activated', () => {
            this.updateStatus({
                updateAvailable: false
            });
        });
    }

    isEnabled() {
        return this.status.enabled;
    }

    update() {
        return new Promise((resolve, reject) => {
            try {
                setTimeout(() => {
                    reject(new Error('Service worker update timed out'));
                }, 30000);

                const wb = new Workbox('/service-worker.js?v=' + Date.now().toString(36));

                wb.addEventListener('controlling', () => {
                    resolve();
                });

                wb.addEventListener('waiting', () => {
                    wb.messageSkipWaiting();
                });

                wb.register();
            } catch (error) {
                console.error('Failed to update service worker:', error);
                reject(error);
            }
        });
    }
}

// Create and export the singleton instance
export default new ServiceWorker();
